<template>
    <!-- Laid out after Rich's mockup on the 86bbrwd3v ticket: one light page, sections split by rules. -->
    <div class="mx-auto max-w-6xl bg-white px-4 py-6 sm:my-6 sm:rounded-2xl sm:px-8 sm:py-8">
        <!-- Gallery: one large photo plus up to four small ones. -->
        <section aria-label="Photos" class="relative">
            <div class="grid h-64 grid-cols-1 gap-2 overflow-hidden rounded-2xl sm:h-80 md:h-[26rem] md:grid-cols-7 md:grid-rows-2">
                <button
                    type="button"
                    :class="['group relative h-full w-full overflow-hidden bg-gray-200 disabled:cursor-default', galleryLayout.main]"
                    :disabled="!canOpenLightbox"
                    :aria-label="canOpenLightbox ? `View photo 1 of ${photoUrls.length}` : property.full_address"
                    @click="openLightbox(0)"
                >
                    <img
                        :src="photoUrls[0]"
                        :alt="property.full_address"
                        :class="['h-full w-full object-cover', canOpenLightbox ? 'transition duration-300 group-hover:scale-[1.02]' : '']"
                        data-test="gallery-main-photo"
                    />
                </button>
                <button
                    v-for="(url, offset) in smallPhotoUrls"
                    :key="`${url}-${offset}`"
                    type="button"
                    :class="['group relative hidden h-full w-full overflow-hidden bg-gray-200 md:block', galleryLayout.small[offset]]"
                    :aria-label="`View photo ${offset + 2} of ${photoUrls.length}`"
                    @click="openLightbox(offset + 1)"
                >
                    <img :src="url" :alt="`${property.full_address} — photo ${offset + 2}`" class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]" />
                </button>
            </div>
            <button
                v-if="showAllPhotosButton"
                type="button"
                class="absolute bottom-3 right-3 inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow ring-1 ring-gray-200 hover:bg-gray-50"
                @click="openLightbox(0)"
            >
                View all {{ photoUrls.length }} photos
            </button>
        </section>

        <!-- Header -->
        <section class="mt-6">
            <div class="flex flex-wrap items-center gap-3">
                <p class="text-4xl font-bold tracking-tight text-gray-900">
                    {{ formatRent(property.rent_currency) }}<span class="text-lg font-medium text-gray-500">/mo</span>
                </p>
                <span
                    v-if="inactive"
                    class="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-700"
                    data-test="status-badge"
                >
                    Not Available
                </span>
                <span
                    v-else
                    class="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-700"
                    data-test="status-badge"
                >
                    For rent
                </span>
            </div>
            <h1 class="mt-1 text-base text-gray-500" data-test="address">
                {{ property.street_address }}<template v-if="property.locality"> · {{ property.locality }}</template>
            </h1>
            <ul class="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500" data-test="header-facts">
                <li v-for="fact in facts" :key="fact.icon" class="inline-flex items-center gap-1.5">
                    <font-awesome-icon :icon="factIcons[fact.icon]" class="h-4 w-4" aria-hidden="true" />
                    <!-- The unit carries its own leading space: Vue drops whitespace between elements. -->
                    <span><span class="font-semibold text-gray-900">{{ fact.value }}</span>{{ fact.unit ? ` ${fact.unit}` : '' }}</span>
                </li>
                <li v-if="availableShort" class="inline-flex items-center gap-1.5" data-test="header-availability">
                    <font-awesome-icon :icon="faCalendar" class="h-4 w-4" aria-hidden="true" />
                    <span>{{ availableShort.prefix ? `${availableShort.prefix} ` : '' }}<span class="font-semibold text-gray-900">{{ availableShort.value }}</span></span>
                </li>
            </ul>
            <ul v-if="highlights.length" class="mt-4 flex flex-wrap gap-2" data-test="highlight-amenities">
                <li
                    v-for="name in highlights"
                    :key="name"
                    class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700"
                >
                    <font-awesome-icon :icon="faCheck" class="h-3 w-3" aria-hidden="true" />
                    {{ name }}
                </li>
            </ul>
        </section>

        <div class="mt-6 border-t border-gray-200" />

        <div class="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-12">
            <!-- Apply card: right under the header on mobile, a sticky right column on large screens. -->
            <aside class="lg:col-start-3 lg:row-start-1">
                <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg shadow-gray-200/60 lg:sticky lg:top-6">
                    <!-- The header right above repeats these on mobile. -->
                    <div class="mb-5 hidden lg:block" data-test="card-summary">
                        <p class="text-2xl font-bold text-gray-900">{{ formatRent(property.rent_currency) }}/mo</p>
                        <p
                            v-if="availableLong"
                            :class="['mt-1 inline-flex items-center gap-1.5 text-sm font-medium', inactive ? 'text-red-600' : 'text-green-600']"
                        >
                            <span :class="['h-2 w-2 rounded-full', inactive ? 'bg-red-500' : 'bg-green-500']" aria-hidden="true" />
                            {{ availableLong.prefix ? `${availableLong.prefix} ` : '' }}{{ availableLong.value }}
                        </p>
                    </div>

                    <div class="space-y-3">
                        <slot name="cta" />
                    </div>

                    <dl class="mt-5 space-y-2.5 border-t border-gray-200 pt-5 text-sm">
                        <div v-if="property.deposit" class="flex justify-between gap-4">
                            <dt class="text-gray-500">Security deposit</dt>
                            <dd class="font-semibold text-gray-900">{{ formatMoney(property.deposit) }}</dd>
                        </div>
                        <div class="flex justify-between gap-4">
                            <dt class="text-gray-500">Application fee</dt>
                            <dd class="font-semibold text-gray-900" data-test="application-fee">{{ formatMoney(property.application_fee ?? 0) }}</dd>
                        </div>
                        <div v-if="property.lease_term" class="flex justify-between gap-4">
                            <dt class="text-gray-500">Lease length</dt>
                            <dd class="font-semibold text-gray-900">{{ formatLeaseTerm(property.lease_term) }}</dd>
                        </div>
                    </dl>

                    <div v-if="property.owner_name" class="mt-5 flex items-center gap-3 border-t border-gray-200 pt-5" data-test="managed-by">
                        <img
                            v-if="property.owner_photo_url && !ownerPhotoFailed"
                            :src="property.owner_photo_url"
                            alt=""
                            class="h-10 w-10 shrink-0 rounded-full object-cover"
                            data-test="managed-by-photo"
                            @error="ownerPhotoFailed = true"
                        />
                        <span v-else class="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-800" aria-hidden="true" />
                        <p class="text-sm font-semibold text-gray-900">Managed by {{ property.owner_name }}</p>
                    </div>
                </div>
            </aside>

            <div class="space-y-10 lg:col-span-2 lg:row-start-1">
                <section v-if="property.description">
                    <h2 class="text-xl font-bold text-gray-900">About this home</h2>
                    <p class="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-600">{{ property.description }}</p>
                </section>

                <section v-if="boxes.length || amenities.length">
                    <h2 class="text-xl font-bold text-gray-900">Facts &amp; features</h2>
                    <div v-if="boxes.length" class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div v-for="box in boxes" :key="box.title" class="rounded-xl bg-gray-50 p-5" data-test="fact-box">
                            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-400">{{ box.title }}</h3>
                            <dl class="mt-3 space-y-2 text-sm">
                                <div v-for="[label, value] in box.rows" :key="label" class="flex justify-between gap-4">
                                    <dt class="text-gray-500">{{ label }}</dt>
                                    <dd class="font-semibold text-gray-900">{{ value }}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                    <ul v-if="amenities.length" class="mt-4 flex flex-wrap gap-2" data-test="amenities">
                        <li
                            v-for="name in amenities"
                            :key="name"
                            class="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-700"
                        >
                            <font-awesome-icon :icon="faCheck" class="h-3 w-3" aria-hidden="true" />
                            {{ name }}
                        </li>
                    </ul>
                </section>
            </div>
        </div>

        <div class="mt-10 border-t border-dashed border-gray-200" />

        <!-- Location spans the full width, below both columns. -->
        <section class="mt-8">
            <h2 class="text-xl font-bold text-gray-900">Location</h2>
            <div class="mt-4 overflow-hidden rounded-2xl border border-gray-200">
                <iframe
                    v-if="mapEmbedUrl"
                    class="block h-72 w-full"
                    style="border:0"
                    loading="lazy"
                    :title="`Map of ${property.full_address}`"
                    :src="mapEmbedUrl"
                    allowfullscreen
                />
                <div class="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-5 py-4 text-sm">
                    <p class="text-gray-600" data-test="map-address">{{ [property.street_address, property.locality].filter(Boolean).join(', ') }}</p>
                    <a
                        :href="directionsUrl(property)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1.5 font-semibold text-gray-900 hover:underline"
                    >
                        Get directions
                        <font-awesome-icon :icon="faArrowRight" class="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                </div>
            </div>

            <ul v-if="nearbyPlaces.length" class="mt-4 flex flex-wrap gap-2" data-test="nearby-places">
                <li
                    v-for="place in nearbyPlaces"
                    :key="place.type"
                    class="inline-flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700"
                    :title="place.label"
                    data-test="nearby-place"
                >
                    <span aria-hidden="true">{{ nearbyIcon(place.type) }}</span>
                    <span data-test="nearby-place-text"><span class="sr-only">{{ place.label }}: </span>{{ place.name }} — {{ formatDistance(place.distance_miles) }}</span>
                </li>
            </ul>
        </section>

        <PhotoLightbox
            :show="lightboxOpen"
            :photos="photoUrls"
            :start-index="lightboxIndex"
            :alt="property.full_address"
            @close="lightboxOpen = false"
        />
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowRight, faBath, faBed, faCalendar, faCheck, faRulerCombined } from '@fortawesome/pro-light-svg-icons'
import PhotoLightbox from './PhotoLightbox.vue'
import {
    availability,
    directionsUrl,
    factBoxes,
    formatDistance,
    formatLeaseTerm,
    formatMoney,
    formatRent,
    galleryPhotoUrls,
    hasUploadedPhotos,
    headerFacts,
    highlightAmenities,
    nearbyIcon,
} from './propertyListing'

const props = defineProps({
    property: {
        type: Object,
        required: true,
    },
    inactive: {
        type: Boolean,
        default: false,
    },
    mapsEmbedKey: {
        type: String,
        default: '',
    },
})

const factIcons = {
    bed: faBed,
    bath: faBath,
    sqft: faRulerCombined,
}

const lightboxOpen = ref(false)
// A photo whose S3 object is gone falls back to the placeholder circle instead of a broken image.
const ownerPhotoFailed = ref(false)
const lightboxIndex = ref(0)

const photoUrls = computed(() => galleryPhotoUrls(props.property))
const smallPhotoUrls = computed(() => photoUrls.value.slice(1, 5))
// Column spans on a 7-column grid, so the large photo takes about 3/7 of the width like the
// mockup, and a property with fewer than five photos has no empty cells.
const galleryLayouts = {
    1: { main: 'md:col-span-7 md:row-span-2', small: [] },
    2: { main: 'md:col-span-4 md:row-span-2', small: ['md:col-span-3 md:row-span-2'] },
    3: { main: 'md:col-span-3 md:row-span-2', small: ['md:col-span-4', 'md:col-span-4'] },
    4: { main: 'md:col-span-3 md:row-span-2', small: ['md:col-span-4', 'md:col-span-2', 'md:col-span-2'] },
    5: { main: 'md:col-span-3 md:row-span-2', small: ['md:col-span-2', 'md:col-span-2', 'md:col-span-2', 'md:col-span-2'] },
}
const galleryLayout = computed(() => galleryLayouts[Math.min(photoUrls.value.length, 5)])
const canOpenLightbox = computed(() => hasUploadedPhotos(props.property))
const showAllPhotosButton = computed(() => canOpenLightbox.value && photoUrls.value.length > 1)
const facts = computed(() => headerFacts(props.property))
const availableShort = computed(() => availability(props.property, { length: 'short', inactive: props.inactive }))
const availableLong = computed(() => availability(props.property, { length: 'long', inactive: props.inactive }))
const highlights = computed(() => highlightAmenities(props.property))
const boxes = computed(() => factBoxes(props.property))
const amenities = computed(() => props.property.amenities || [])
const nearbyPlaces = computed(() => props.property.nearby_places || [])

// The Maps Embed key comes from the server (config services.google_maps_embed.key). With no
// key the map is dropped and the address bar plus "Get directions" still stand on their
// own, so a page that forgets to pass it degrades rather than showing Google's error tile.
const mapEmbedUrl = computed(() => {
    if (! props.mapsEmbedKey || ! props.property.map_address) {
        return null
    }

    // map_address already joins its parts with '+' (Property::getMapAddressAttribute), so those
    // go back to spaces before encoding: encoding them as %2B would have Google look for a
    // literal plus, while leaving the value raw breaks any address holding a '#' or '&'.
    const destination = encodeURIComponent(props.property.map_address.replace(/\+/g, ' '))

    return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(props.mapsEmbedKey)}&q=${destination}`
})

const openLightbox = (index) => {
    if (!canOpenLightbox.value) {
        return
    }
    lightboxIndex.value = index
    lightboxOpen.value = true
}
</script>
